import type { DigitalSignatureIntegrity } from '@aline/common';
import {
  DIGITAL_SIGNATURE_INTEGRITIES,
  DIGITAL_SIGNATURE_KINDS,
  getIsDigitalSignatureIntegrity,
} from '@aline/common';
import type { Apollo } from 'src/gql/generated';

/** One digital signature as the side panel shows it. */
export interface DigitalSignatureCard {
  /** Whether the signed bytes are unchanged, which picks the card's icon. Says nothing about who the signer is. */
  isValid: boolean;
  key: string;
  meta: string;
  name: string;
  role: string;
}

type Detection = Pick<
  Apollo.SignatureDetectionDetailsFragment,
  'digitalSignatures' | 'hasCryptographicSignature'
>;

/**
 * The signer's certificate is not checked against a trusted authority, so an
 * intact signature proves the bytes are unchanged, not who signed them.
 */
const INTEGRITY_NOTES: Record<DigitalSignatureIntegrity, string> = {
  altered: 'The file changed after signing, so the signature no longer matches it',
  intact: 'Unchanged since signing; signer identity not checked',
  rewritten: 'The file was rewritten after signing, so the signature cannot be checked',
  unverified: 'Could not be verified',
};

/**
 * A card per digital signature, in signing order. Document timestamps name no
 * signer and are left out. A run that knows the PDF is cryptographically
 * signed but has no list (recorded before the list existed) gets one plain card.
 */
export function getDigitalSignatureCards(
  detection: Detection | null | undefined
): DigitalSignatureCard[] {
  const signatures = (detection?.digitalSignatures ?? []).filter(
    signature => signature.kind === DIGITAL_SIGNATURE_KINDS.signature
  );
  if (!signatures.length)
    return detection?.hasCryptographicSignature
      ? [
          {
            isValid: true,
            key: 'digital-0',
            meta: '',
            name: 'Digitally signed',
            role: 'The PDF carries a cryptographic signature.',
          },
        ]
      : [];

  return signatures.map((signature, index) => {
    const { certificate } = signature;
    // The certificate names the key that signed; `/Name` is free text the signing software wrote.
    const name = certificate?.commonName ?? signature.name ?? 'Digital signature';
    const organization = certificate?.organization === name ? null : certificate?.organization;
    const integrity = getIsDigitalSignatureIntegrity(signature.integrity)
      ? signature.integrity
      : DIGITAL_SIGNATURE_INTEGRITIES.unverified;
    const isIntact = integrity === DIGITAL_SIGNATURE_INTEGRITIES.intact;
    const note =
      isIntact && !signature.coversWholeFile
        ? 'Unchanged since signing, but the file has content this signature does not cover'
        : INTEGRITY_NOTES[integrity];

    return {
      isValid: isIntact,
      key: `digital-${index}`,
      meta: [signature.signedAt && `Signed ${formatSignedAt(signature.signedAt)}`, note]
        .filter(Boolean)
        .join(' · '),
      name,
      role: [
        organization,
        certificate?.email,
        signature.name && signature.name !== name && `named ${signature.name} in the PDF`,
      ]
        .filter(Boolean)
        .join(', '),
    };
  });
}

/** A signing time as "May 9, 2024", in the viewer's timezone. */
function formatSignedAt(signedAt: string): string {
  const date = new Date(signedAt);
  if (Number.isNaN(date.getTime())) return signedAt;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}
